import { customAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { urlToPattern } from "../urlToPattern";
import logo from "./logo.svg";

/** AS2 endpoints are a single path rather than an API surface, so the pattern has
 * to match the endpoint itself as well as anything beneath it. */
function endpointPattern(endpointUrl: string | undefined) {
  const pattern = urlToPattern(endpointUrl);
  return pattern === ".*" ? pattern : `${pattern.slice(0, -3)}(?:/.*)?`;
}

export const as2 = defineConnector({
  identifier: "as2",
  name: "AS2",
  tagLine: "Exchange EDI with trading partners over AS2",
  keywords: "edi edifact x12 as2 ediint mdn trading partner b2b smime",
  logo,
  protocol: "http",
  tags: ["data", "custom"],
  defaultUrlPattern: ({ endpointUrl }) => endpointPattern(endpointUrl),
  auth: (app) =>
    customAuth("as2", {
      as2From: app.config.as2From,
      as2To: app.config.as2To,
      certificate: app.config.certificate,
      partnerCertificate: app.config.partnerCertificate,
      encryptionAlgorithm: app.config.encryptionAlgorithm,
      payloadContentType: app.config.payloadContentType,
      mdnTo: app.config.mdnTo,
      privateKey: app.secret.privateKey,
    }),
  aiContext:
    "Sending is outbound only. Post the plaintext EDI interchange as the request body; 3B signs it, encrypts it to the partner's certificate, and sets the AS2 headers. The response is the partner's MDN receipt. Receiving partner-originated AS2 documents is not supported.",
  steps: [
    {
      title: "Exchange details with your trading partner",
      items: [
        {
          kind: "markdown",
          content:
            'AS2 needs both sides to swap details before anything will work. From your partner you need their **endpoint URL**, their **AS2 ID**, and their **public certificate**. You give them your own AS2 ID and the public half of the certificate you upload here.\n\nIf you don’t have a certificate yet, generate a self-signed one — AS2 partners exchange certificates directly rather than relying on a public CA:\n\n```\nopenssl req -x509 -newkey rsa:2048 -sha256 -days 1095 \\\n  -keyout as2-private.pem -out as2-public.pem -nodes \\\n  -subj "/CN=your-as2-id"\n```\n\nUpload `as2-private.pem` and `as2-public.pem` below, and send `as2-public.pem` to your partner.',
        },
      ],
    },
    {
      title: "Enter your AS2 connection",
      items: [
        {
          kind: "input",
          label: "Name",
          required: true,
          defaultValue: "AS2",
          target: { kind: "connectedApp", field: "name" },
        },
        {
          kind: "baseUrl",
          label: "Partner endpoint URL",
          required: true,
          placeholder: "https://partner.example.com/as2/receive",
          helperText: "The HTTPS URL your partner receives AS2 messages on.",
          target: { kind: "configuration", field: "endpointUrl" },
        },
        {
          kind: "input",
          label: "Your AS2 ID",
          required: true,
          placeholder: "YOURCOMPANY",
          helperText:
            "Sent as the AS2-From header. Your partner must have this on file.",
          target: { kind: "configuration", field: "as2From" },
        },
        {
          kind: "input",
          label: "Partner AS2 ID",
          required: true,
          placeholder: "PARTNERCO",
          helperText:
            "Sent as the AS2-To header, exactly as your partner specified it.",
          target: { kind: "configuration", field: "as2To" },
        },
        {
          kind: "file",
          label: "Your certificate (public)",
          accept: ".pem,.crt,.cer",
          required: true,
          helperText:
            "The certificate identifying you. It is sent with each message so your partner can verify the signature.",
          target: { kind: "configuration", field: "certificate" },
        },
        {
          kind: "file",
          label: "Your private key",
          accept: ".pem,.key",
          required: true,
          helperText:
            "PKCS#8 PEM. Used to sign messages inside 3B’s proxy — workflow code never receives it.",
          target: { kind: "secrets", field: "privateKey" },
        },
        {
          kind: "file",
          label: "Partner certificate (public)",
          accept: ".pem,.crt,.cer",
          required: true,
          helperText:
            "Your partner’s public certificate. Messages are encrypted to it, and it verifies their MDN receipts.",
          target: { kind: "configuration", field: "partnerCertificate" },
        },
        {
          kind: "select",
          label: "Encryption",
          required: true,
          defaultValue: "aes256",
          helperText: "Must match what your partner expects.",
          target: { kind: "configuration", field: "encryptionAlgorithm" },
          options: [
            { value: "aes256", label: "AES-256-CBC" },
            { value: "aes128", label: "AES-128-CBC" },
          ],
        },
        {
          kind: "select",
          label: "Payload content type",
          required: true,
          defaultValue: "application/edi-x12",
          target: { kind: "configuration", field: "payloadContentType" },
          options: [
            {
              value: "application/edi-x12",
              label: "X12 (application/edi-x12)",
            },
            {
              value: "application/edifact",
              label: "EDIFACT (application/edifact)",
            },
            { value: "application/xml", label: "XML (application/xml)" },
            {
              value: "application/octet-stream",
              label: "Other (application/octet-stream)",
            },
          ],
        },
        {
          kind: "input",
          label: "Request MDN receipts to",
          placeholder: "edi@yourcompany.com",
          helperText:
            "Optional. An email address or URL identifying you as the receipt requester. Leave blank to request no receipt.",
          target: { kind: "configuration", field: "mdnTo" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ endpointUrl }) => ({
      url: endpointUrl ?? "",
      method: "POST",
      body: "3B AS2 connection test",
    }),
  },
  // The partner certificate and the AS2 IDs are not secret, and step code needs
  // them to verify the signature on an MDN receipt.
  publicEnvVars: ({
    endpointUrl,
    as2From,
    as2To,
    partnerCertificate,
    payloadContentType,
  }) => ({
    AS2_ENDPOINT_URL: endpointUrl ?? "",
    AS2_FROM: as2From ?? "",
    AS2_TO: as2To ?? "",
    AS2_PARTNER_CERTIFICATE: partnerCertificate ?? "",
    AS2_PAYLOAD_CONTENT_TYPE: payloadContentType ?? "application/edi-x12",
  }),
  usageHint: () =>
    "POST the plaintext EDI interchange to AS2_ENDPOINT_URL. 3B signs it, encrypts it to the partner certificate, and sets AS2-From, AS2-To, and Message-ID — don't set those or build the S/MIME yourself. A 2xx response body is the partner's MDN receipt; verify its signature against AS2_PARTNER_CERTIFICATE and correlate it to the Message-ID from the response headers. A 403 with an `as2_` error type means the connector is misconfigured and nothing was sent.",
});
